// Wizard Step 2 — Шалтгаан (screens-buyer-app §5).
//
// RETURN reasons when the order is delivered; SIMPLE reasons when not. Reason
// radio cards (responsive 2-col), C8 sub-reason dropdown (hardcoded taxonomy
// until the backend enforces it), and a detail textarea required for
// OTHER (>=10 chars) / PRICE_DISPUTE / RECALL.
import { HelpIcon } from "@dehub/theme";
import { Box, Select, SimpleGrid, Stack, Text, Textarea, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { ReturnReason } from "../../../../apis/return";
import { RETURN_REASONS, reasonLabelMn, SIMPLE_REASONS } from "../constants";
import { reasonRequiresDetail } from "./state";
import { subReasonsFor } from "./sub-reasons";
import classes from "./wizard-stepper.module.css";

interface Props {
  isDelivered: boolean;
  reason: ReturnReason | null;
  subReason: string | null;
  reasonDetails: string;
  onReason: (reason: ReturnReason) => void;
  onSubReason: (value: string | null) => void;
  onReasonDetails: (value: string) => void;
}

function ReasonCard({
  reason,
  active,
  onSelect,
}: {
  reason: ReturnReason;
  active: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation("buyerApp");
  return (
    <UnstyledButton className={classes.reason} role="radio" aria-checked={active} onClick={onSelect}>
      <Box component="span" className={classes.radio} aria-hidden />
      <Box miw={0}>
        <Text className={classes.reasonLabel}>{reasonLabelMn(reason)}</Text>
        <Text className={classes.reasonHint}>{t(`stepReason.hint.${reason}`, reason)}</Text>
      </Box>
    </UnstyledButton>
  );
}

export function StepReason({
  isDelivered,
  reason,
  subReason,
  reasonDetails,
  onReason,
  onSubReason,
  onReasonDetails,
}: Props) {
  const { t } = useTranslation("buyerApp");
  const reasons = isDelivered ? RETURN_REASONS : SIMPLE_REASONS;
  const subOptions = subReasonsFor(reason);
  const detailRequired = reasonRequiresDetail(reason);
  const trimmedLen = reasonDetails.trim().length;
  const detailError =
    detailRequired && reason === "OTHER" && trimmedLen > 0 && trimmedLen < 10
      ? t("stepReason.minChars")
      : null;

  return (
    <Stack gap="md">
      <Box>
        <Text className={classes.title}>{t("stepReason.title")}<HelpIcon helpKey="order.buyer-app-my-returns-new.section.reason" variant="section" /></Text>
        <Text className={classes.subtitle}>
          {isDelivered ? t("stepReason.deliveredHint") : t("stepReason.notDeliveredHint")}
        </Text>
      </Box>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={10} role="radiogroup" aria-label={t("stepReason.radioGroupAria")}>
        {reasons.map((r) => (
          <ReasonCard key={r} reason={r} active={reason === r} onSelect={() => onReason(r)} />
        ))}
      </SimpleGrid>

      {subOptions.length > 0 ? (
        <Select
          label={t("stepReason.subReasonLabel")}
          placeholder={t("stepReason.subReasonPlaceholder")}
          data={subOptions.map((o) => ({ value: o.value, label: o.labelMn }))}
          value={subReason}
          onChange={onSubReason}
          clearable
          w={{ base: "100%", sm: 320 }}
        />
      ) : null}

      <Textarea
        label={detailRequired ? t("stepReason.detailRequiredLabel") : t("stepReason.detailOptionalLabel")}
        placeholder={t("stepReason.detailPlaceholder")}
        value={reasonDetails}
        onChange={(e) => onReasonDetails(e.currentTarget.value)}
        autosize
        minRows={3}
        maxRows={6}
        maxLength={500}
        withAsterisk={detailRequired}
        error={detailError}
        description={`${reasonDetails.length} / 500`}
        inputWrapperOrder={["label", "input", "error", "description"]}
      />
    </Stack>
  );
}
